// components/map/mobile-journey-options.tsx
"use client";

import React from "react";

import { GradientOutlineButton } from "@/components/design/gradient-outline-button"; // Adjust import path as needed

import { type GeoJSONLineStringFeature } from "./map"; // Adjust import path as needed
import { JOURNEY_ROUTES, type JourneyRoute } from "./map-data"; // Adjust import path as needed

// Re-using JourneyOptionProps and JourneyOption from the original file for consistency
interface JourneyOptionProps {
  color: string;
  title: string;
  subtitle: string;
  time: string;
  onClick: (geoJSON: GeoJSONLineStringFeature) => void;
  geoJSON: GeoJSONLineStringFeature;
  isSelected: boolean;
}

const JourneyOption: React.FC<JourneyOptionProps> = ({
  color,
  title,
  subtitle,
  time,
  onClick,
  geoJSON,
  isSelected,
}) => (
  <div
    className={`flex items-center justify-between py-2 rounded-2xl cursor-pointer transition-all duration-100
      ${
        isSelected
          ? "bg-[radial-gradient(circle_at_center,hsla(205,20%,50%,0.4)_0%,transparent_100%)]"
          : "hover:bg-[radial-gradient(circle_at_center,hsla(205,20%,50%,0.2)_0%,transparent_100%)]"
      }`}
    onClick={() => onClick(geoJSON)}
  >
    <div className="flex items-center">
      <div className={`mr-6 h-12 w-1 rounded-full ${color}`}></div>
      <div>
        <p className="text-p1 font-semibold">{title}</p>
        <p className="text-p2">{subtitle}</p>
      </div>
    </div>
    <span className="text-p1 font-semibold">{time}</span>
  </div>
);

interface MobileJourneyOptionsProps {
  onRouteSelect: (geoJSON: GeoJSONLineStringFeature) => void;
  selectedRouteGeoJSON: GeoJSONLineStringFeature;
}

const MobileJourneyOptions: React.FC<MobileJourneyOptionsProps> = ({
  onRouteSelect,
  selectedRouteGeoJSON,
}) => {
  const routeUrl: string | null = (selectedRouteGeoJSON?.properties?.url as string) || null;

  return (
    <div
      // Mobile-only specific styles:
      // Always visible, full width, normal document flow (relative positioning)
      // Hide on medium screens and up (md:hidden) bg-[linear-gradient(to_right,#010C15CC_0%,rgba(1,12,21,0.06)_50%,#010C15CC_100%)]
      className={`relative z-10 flex flex-col justify-between
                  w-full px-4 py-4 text-white overflow-hidden 
                  backdrop-blur-[22px]
                  lg:hidden
      `}
    >
      {/* Header text - always visible, no collapse logic */}
      <div className="flex flex-col">
        <p className="text-h4">Wskazówki dojazdu</p>
        <p className="text-p opacity-50">Czas i środek transportu</p>
      </div>

      {/* Content wrapper - always visible, no collapse logic */}
      <div className="flex flex-col gap-2 max-h-[500px] mt-4"> {/* Added mt-4 for spacing */}
        {/* Route options list */}
        {JOURNEY_ROUTES.map((route: JourneyRoute, index) => (
          <React.Fragment key={index}>
            <div className="ml-1 border-b border-gray-700"></div>
            <JourneyOption
              {...route}
              onClick={onRouteSelect}
              geoJSON={route.geoJSON}
              isSelected={route.geoJSON === selectedRouteGeoJSON}
            />
          </React.Fragment>
        ))}
        {/* <div className="ml-1 border-b border-gray-700"></div> */}

        {/* "Wyznacz trasę" button */}
        <div className="h-12 mt-2"> {/* Added mt-2 for spacing */}
          <a
            href={routeUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={{ pointerEvents: routeUrl ? "auto" : "none" }}
            className={routeUrl ? "" : "opacity-50 cursor-not-allowed"}
          >
            <GradientOutlineButton disabled={!routeUrl}>
              Wyznacz trasę
            </GradientOutlineButton>
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileJourneyOptions;